// src/routes/PrivateRoute.js
import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import { verifyUser } from "../store/Auth/authThunks";
import PrivateLayout from "./PrivateLayout";
import { setAuth0Instance } from "../utils/auth0";

import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

export default function PrivateRoute({ children }) {
  const dispatch = useDispatch();
  const auth0 = useAuth0();
  const { isAuthenticated, isLoading } = auth0;

  const backendUser = useSelector((state) => state.auth.user);
  const authInitialized = useSelector((state) => state.auth.initialized);
  const authLoading = useSelector((state) => state.auth.loading);
  const verificationAttempted = useSelector((state) => state.auth.verificationAttempted);

  // Set Auth0 instance globally for API client interceptor
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setAuth0Instance(auth0);
    }
  }, [auth0, isLoading, isAuthenticated]);

  // Run verifyUser ONCE when Auth0 finishes loading
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (verificationAttempted) return; // Already tried - NEVER retry
    if (backendUser) return; // Already have user

    dispatch(verifyUser()); // Token is automatically added by interceptor
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, verificationAttempted, dispatch]);

  // Auth0 NOT ready yet → spinner
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Auth0 finished but NOT logged in → go to public
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Auth0 logged in → backend verifyUser still running → spinner
  if (!authInitialized || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If backend does not recognize user after verification → logout and force re-auth
  if (!backendUser && authInitialized) {
    console.error("❌ Backend verification failed - logging out");
    auth0.logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
    return null;
  }

  // Success — render with layout (includes SideMenu)
  return <PrivateLayout>{children}</PrivateLayout>;
}
